/**
 * 인증 컨텍스트
 * React Context를 통해 전역 인증 상태를 관리합니다.
 */

/* eslint-disable react-refresh/only-export-components */
import { createContext, useCallback, useEffect, useMemo, useState } from "react";
import { flushSync } from "react-dom";
import type { ReactNode } from "react";
import * as authService from "../services/auth.service";
import type { SocialProvider } from "../services/auth.service";
import type {
  AuthState,
  AuthUser,
  ServiceResult,
} from "../services/types";

/** 인증 컨텍스트 값 타입 */
export interface AuthContextValue extends AuthState {
  signInWithSocialProvider: (provider: SocialProvider) => Promise<ServiceResult<AuthUser>>;
  signOut: () => Promise<ServiceResult<void>>;
}

/** 초기 상태 */
const initialState: AuthState = {
  isAuthenticated: false,
  user: null,
  isLoading: true,
};

/** 인증 컨텍스트 (기본값 undefined로 Provider 체크) */
export const AuthContext = createContext<AuthContextValue | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

/** 인증 Provider 컴포넌트 */
export function AuthProvider({ children }: AuthProviderProps) {
  const [state, setState] = useState<AuthState>(initialState);

  useEffect(() => {
    let mounted = true;

    async function initialize() {
      try {
        const authState = await authService.initializeAuth();
        if (mounted) {
          setState(authState);
        }
      } catch (error) {
        console.error('[AuthContext] 인증 초기화 실패:', error);
        if (mounted) {
          setState({
            isAuthenticated: false,
            user: null,
            isLoading: false,
          });
        }
      }
    }

    const timeoutId = setTimeout(() => {
      if (mounted) {
        console.warn('[AuthContext] 인증 초기화 타임아웃');
        setState((prevState) => ({ ...prevState, isLoading: false }));
      }
    }, 10000);

    initialize();

    return () => {
      mounted = false;
      clearTimeout(timeoutId);
    };
  }, []);

  // Idempotent 상태 업데이트 - 동일한 값이면 리렌더링 스킵하여 경합 방지
  const setAuthState = useCallback((user: AuthUser | null) => {
    setState(prev => {
      const isAuthenticated = user !== null;
      if (prev.isAuthenticated === isAuthenticated && prev.user?.id === user?.id && !prev.isLoading) {
        return prev;
      }
      return { isAuthenticated, user, isLoading: false };
    });
  }, []);

  useEffect(() => {
    return authService.onAuthStateChange(setAuthState);
  }, [setAuthState]);

  const signInWithSocialProvider = useCallback(async (provider: SocialProvider) => {
    const result = await authService.signInWithSocialProvider(provider);
    if (result.success) {
      // flushSync로 동기 커밋 - router.push 전에 isAuthenticated=true 보장
      flushSync(() => {
        setAuthState(result.data);
      });
    }
    return result;
  }, [setAuthState]);

  const signOut = useCallback(async () => {
    const result = await authService.signOut();
    if (result.success) {
      flushSync(() => {
        setAuthState(null);
      });
    }
    return result;
  }, [setAuthState]);

  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      signInWithSocialProvider,
      signOut,
    }),
    [state, signInWithSocialProvider, signOut]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
